import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import { Mail, ShieldCheck, ArrowLeft } from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const navigate = useNavigate();
  const appName = process.env.REACT_APP_APP_NAME || 'CRM Automax';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api('/users/forgot-password', {
        method: 'POST',
        body: { email }
      });
      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message);
        setTimeout(() => {
          navigate(`/verify-otp?email=${encodeURIComponent(email)}`);
        }, 2000);
      } else {
        setError(data.message || 'Something went wrong');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '13px 13px 13px 44px',
    borderRadius: '10px', border: '1px solid #e2e8f0',
    fontSize: '14px', outline: 'none',
    background: '#ffffff', color: '#1e293b',
    transition: 'all 0.2s ease', boxSizing: 'border-box',
  };

  return (
    <div className="auth-page-wrapper" style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#f8fafc',
      padding: '24px', fontFamily: "'Inter', sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        @keyframes fadeUp { from{opacity:0;transform:translateY(15px);}to{opacity:1;transform:translateY(0);} }
        .login-input:focus { border-color: #3b82f6 !important; box-shadow: 0 0 0 4px rgba(59,130,246,0.1); }
        .login-btn:hover:not(:disabled) { background: #2563eb !important; transform: translateY(-1px); }
        .login-btn:active:not(:disabled) { transform: translateY(0); }
      `}</style>

      <div className="auth-card-container" style={{
        width: '100%', maxWidth: '400px',
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '20px', padding: '48px 36px',
        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.05)',
        animation: 'fadeUp 0.5s ease-out',
      }}>

        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '14px',
            background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px', boxShadow: '0 8px 20px rgba(59,130,246,0.2)',
          }}>
            <ShieldCheck size={26} color="white" />
          </div>
          <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', marginBottom: '8px', letterSpacing: '-0.02em' }}>
            Forgot Password
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px' }}>Enter your email to receive an OTP.</p>
        </div>

        {error && (
          <div style={{
            background: '#fef2f2',
            color: '#b91c1c', padding: '14px 16px', borderRadius: '10px',
            fontSize: '13px', marginBottom: '24px',
            border: '1px solid #fee2e2',
            lineHeight: 1.5,
          }}>
            {error}
          </div>
        )}
        
        {success && (
          <div style={{
            background: '#ecfdf5',
            color: '#059669', padding: '14px 16px', borderRadius: '10px',
            fontSize: '13px', marginBottom: '24px',
            border: '1px solid #a7f3d0',
            lineHeight: 1.5,
          }}>
            {success} Redirecting...
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '8px', marginLeft: '2px' }}>
              Email Address
            </label>
            <div style={{ position: 'relative' }}>
              <Mail size={17} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                className="login-input" type="email"
                value={email} onChange={e => setEmail(e.target.value)}
                required placeholder="name@company.com" style={inputStyle}
                disabled={loading || success}
              />
            </div>
          </div>

          <button
            type="submit" disabled={loading || success || !email} className="login-btn"
            style={{
              width: '100%', padding: '14px',
              background: (loading || success || !email) ? '#93c5fd' : '#3b82f6',
              color: 'white', border: 'none', borderRadius: '10px',
              fontSize: '15px', fontWeight: '600',
              cursor: (loading || success || !email) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              marginTop: '4px', transition: 'all 0.2s ease',
            }}
          >
            {loading ? 'Sending OTP...' : 'Send OTP'}
          </button>
        </form>

        <div style={{ marginTop: '32px', textAlign: 'center', fontSize: '14px' }}>
          <Link to="/login" style={{ color: '#64748b', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <ArrowLeft size={16} /> Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
